import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, ProgressBar } from 'react-bootstrap';
import { Upload, FileSpreadsheet, CheckCircle, AlertTriangle } from 'lucide-react';

const ImportArticlesModal = ({ isOpen, onClose, onImported }) => {
    const [file, setFile] = useState(null);
    const [createMissing, setCreateMissing] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (selected) {
            const ext = selected.name.split('.').pop().toLowerCase();
            if (!['csv', 'xlsx'].includes(ext)) {
                alert('Nur CSV- und XLSX-Dateien werden unterstützt.');
                return;
            }
            setFile(selected);
            setResult(null);
        }
    };

    const handleImport = async () => {
        if (!file) return;

        setIsUploading(true);
        setResult(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('createMissingCategories', createMissing ? '1' : '0');

        try {
            const response = await fetch('index.php/articles/importArticles', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            setResult(data);

            if (data.success && data.added > 0) {
                onImported?.();
            }
        } catch (err) {
            setResult({ success: false, error: 'Netzwerkfehler beim Import' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setResult(null);
        setIsUploading(false);
        onClose();
    };

    const handleDownloadTemplate = () => {
        const bom = '\uFEFF';
        const header = 'Titel;Beschreibung;Kategorie;Unterkategorie;Artikelnummer;Netto/Brutto;Preis Netto;Preis Brutto;Steuer-ID;Einheit;Anzahl';
        const example = 'Fotoshooting 1h;Einzelshooting im Studio;Shootings;Portrait;01010001;netto;150,00;;1;1;1';
        const csv = bom + header + '\n' + example + '\n';

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'artikel-vorlage.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal show={isOpen} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton style={{
                background: 'var(--bg-secondary, #f5f7f9)',
                borderBottom: '1px solid var(--border-light, #e2e8f0)'
            }}>
                <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary, #2c3e50)' }}>
                    <Upload size={20} style={{ marginRight: '0.5rem' }} />
                    Artikel importieren
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '1.5rem' }}>
                {/* Ergebnis */}
                {result && (
                    <Alert variant={result.success && result.errors === 0 ? 'success' : result.success ? 'warning' : 'danger'} className="mb-3">
                        {result.success ? (
                            <>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    {result.errors === 0 ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                                    <strong>Import abgeschlossen</strong>
                                </div>
                                <div style={{ fontSize: '0.875rem' }}>
                                    <span style={{ color: 'var(--bs-success, #198754)' }}>{result.added} importiert</span>
                                    {result.skipped > 0 && <>, <span className="text-muted">{result.skipped} übersprungen</span></>}
                                    {result.errors > 0 && <>, <span style={{ color: 'var(--bs-danger, #dc3545)' }}>{result.errors} Fehler</span></>}
                                </div>
                                {result.error_details?.length > 0 && (
                                    <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', maxHeight: '150px', overflowY: 'auto' }}>
                                        {result.error_details.map((err, i) => (
                                            <div key={i} style={{ color: 'var(--bs-danger, #dc3545)' }}>{err}</div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>{result.error}</>
                        )}
                    </Alert>
                )}

                {/* Datei-Upload */}
                <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: '2px dashed var(--border-medium, #cbd5e1)',
                        borderRadius: '12px',
                        padding: '2rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: file ? 'var(--bg-hover, #f8fafc)' : 'transparent',
                        transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent-slate, #5b7c8d)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-medium, #cbd5e1)'; }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    {file ? (
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <FileSpreadsheet size={24} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                            <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary, #2c3e50)' }}>{file.name}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)' }}>
                                    {(file.size / 1024).toFixed(1)} KB — Klicken zum Ändern
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Upload size={32} style={{ color: 'var(--text-secondary, #64748b)', marginBottom: '0.5rem' }} />
                            <div style={{ color: 'var(--text-primary, #2c3e50)', fontWeight: 500 }}>
                                CSV oder XLSX Datei auswählen
                            </div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)', marginTop: '0.25rem' }}>
                                Maximal 5 MB
                            </div>
                        </>
                    )}
                </div>

                {/* Optionen */}
                <div style={{ marginTop: '1rem' }}>
                    <Form.Check
                        type="checkbox"
                        id="createMissingCategories"
                        label="Fehlende Kategorien und Unterkategorien automatisch erstellen"
                        checked={createMissing}
                        onChange={(e) => setCreateMissing(e.target.checked)}
                        style={{ fontSize: '0.875rem' }}
                    />
                </div>

                {/* Vorlage */}
                <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'var(--bg-secondary, #f5f7f9)', borderRadius: '8px', fontSize: '0.8rem' }}>
                    <strong>Erwartete Spalten:</strong> Titel*, Beschreibung, Kategorie, Unterkategorie, Artikelnummer, Netto/Brutto, Preis Netto, Preis Brutto, Steuer-ID, Einheit, Anzahl
                    <div style={{ marginTop: '0.5rem' }}>
                        <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); handleDownloadTemplate(); }}
                            style={{ color: 'var(--accent-slate, #5b7c8d)', textDecoration: 'underline' }}
                        >
                            CSV-Vorlage herunterladen
                        </a>
                    </div>
                </div>

                {isUploading && (
                    <ProgressBar animated now={100} style={{ marginTop: '1rem' }} />
                )}
            </Modal.Body>
            <Modal.Footer style={{
                borderTop: '1px solid var(--border-light, #e2e8f0)',
                background: 'var(--bg-secondary, #f5f7f9)',
            }}>
                <Button variant="outline-secondary" onClick={handleClose} disabled={isUploading}>
                    {result?.success ? 'Schließen' : 'Abbrechen'}
                </Button>
                {!result?.success && (
                    <Button
                        variant="primary"
                        onClick={handleImport}
                        disabled={!file || isUploading}
                    >
                        {isUploading ? 'Importiere...' : 'Importieren'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ImportArticlesModal;
